import {Injectable} from '@angular/core';
import {LabelExpressionUtilityService} from '../label-expression-utility-service';
import {LookupService} from '../../../../common/service/lookup.service';
import {labelProperties, Attributes} from '../../../../common/models/label-expression.model';
import {AnnotationConfigLookup} from '../../../../common/models/annotation-config.model';

@Injectable({
    providedIn: 'root'
})
export class LineLabelExpressionBuilderService {
    title = 'LineLabelExpressionBuilder';

    fontsizeItalics = 10;
    fontsizeUpright = 7.2;

    constructor(
        private labelExpressionUtilityService: LabelExpressionUtilityService,
        private lookupService: LookupService
    ) {}

    build(attributes: Attributes) {
        console.debug(this.title + ': build: line feature subtype = ' + attributes.featuresubtype);

        let attributeLabel = '';
        const parentAnnotations = [];

        const annotationConfigurations: AnnotationConfigLookup[] = this.lookupService
            .getLookups('annotationConfig')
            .filter(
                (ac: AnnotationConfigLookup) => ac.featureType === 'line' && ac.featureSubTypes?.split(',').includes(`${attributes.featuresubtype}`)
            );

        annotationConfigurations.forEach(row => {
            if (Object.hasOwn(attributes, row.annotationAttribute) && attributes[row.annotationAttribute]) {
                let labelValue = this.getLabelValue(row, attributes);
                if (!row.parent) {
                    parentAnnotations.push({id: row.id, value: labelValue});
                } else {
                    const attributeValueParent = parentAnnotations.filter(function (parentAnnotation) {
                        return parentAnnotation.id === row.parent;
                    });
                    if (attributeValueParent && attributeValueParent.length) {
                        attributeLabel = attributeValueParent[0].value + ' ';
                    } else {
                        labelValue = '';
                    }
                }

                attributeLabel += labelValue;
            }
        });

        return this.buildLabelExpression(attributes.featuresubtype, attributeLabel);
    }

    getLabelValue(row: AnnotationConfigLookup, attributes: Attributes) {
        let attributeLabel = '';
        switch (row.featureAttribute) {
            case 'distance':
                attributeLabel = isNaN(attributes[row.annotationAttribute])
                    ? attributes[row.annotationAttribute]
                    : this.labelExpressionUtilityService.round(attributes[row.annotationAttribute], 2);
                break;
            case 'bearing':
                attributeLabel = this.formatBearing(`${attributes[row.annotationAttribute]}`);
                break;
            case 'measurementsource':
                if (this.getMeasureSourceExpression(attributes[row.annotationAttribute])) {
                    console.debug(this.title + ': build: attribute label for measurement source = ' + attributeLabel);
                    const lineMeasurementSourceValue = this.lookupService
                        .getLookups(row.lookupService)
                        .find(l => this.labelExpressionUtilityService.isLookupItemDescriptionMatch(l, attributes[row.annotationAttribute]));
                    attributeLabel = lineMeasurementSourceValue.code;
                }
                break;
            default:
                break;
        }

        return attributeLabel;
    }

    buildLabelExpression(featureSubtype: number, label: string) {
        let fontExpression: string;
        const labels = [];
        const labelProperties = {} as labelProperties;

        if (featureSubtype === 8 || featureSubtype === 9 || featureSubtype === 27) {
            fontExpression = this.labelExpressionUtilityService.formatLabel(label.trim(), this.fontsizeUpright, true);
            labels.push(this.labelExpressionUtilityService.getLabelProperties('7.2em', 'normal', 'middle', label.trim()));
        } else {
            fontExpression = this.labelExpressionUtilityService.formatLabel(label.trim(), this.fontsizeItalics, false);
            labels.push(this.labelExpressionUtilityService.getLabelProperties('10em', 'italic', 'middle', label.trim()));
        }

        labelProperties.labels = labels;
        labelProperties.labelExpression = fontExpression;

        return labelProperties;
    }

    getMeasureSourceExpression(attribute: string) {
        return attribute === 'Calculated' || attribute === 'Original' || attribute === 'Per Original';
    }

    formatBearing(bearing: string) {
        if (!bearing) {
            return '';
        }

        const degreesMinutesSeconds = bearing.split(':');
        const degrees = degreesMinutesSeconds[0];
        const minutes = degreesMinutesSeconds.length > 1 ? degreesMinutesSeconds[1] : undefined;
        const seconds = degreesMinutesSeconds.length > 2 ? degreesMinutesSeconds[2] : undefined;

        const formattedDegrees = parseInt(degrees, 10).toString();

        let formattedMinutes = minutes;
        if (minutes) {
            formattedMinutes = parseInt(minutes, 10).toString();
        }

        let formattedSeconds = seconds;
        if (seconds) {
            formattedSeconds = parseInt(seconds, 10).toString();
        }

        let formattedBearing = formattedDegrees;

        formattedBearing += '°';
        if (formattedSeconds && formattedSeconds !== '0') {
            formattedBearing += formattedMinutes;
            formattedBearing += "'";
            formattedBearing += formattedSeconds;
            formattedBearing += '"';
        } else {
            if (formattedMinutes && formattedMinutes !== '0') {
                formattedBearing += formattedMinutes;
                formattedBearing += "'";
            }
        }

        if (formattedDegrees === '0' && formattedMinutes === '0' && formattedSeconds === '0') {
            formattedBearing += '0';
            formattedBearing += "'";
            formattedBearing += '0';
            formattedBearing += '"';
        }

        return formattedBearing;
    }
}
